type WidgetFn = (command: string, ...args: unknown[]) => void;

type WidgetWindow = Window & { RogerIQ?: WidgetFn };

type IdentityContext = {
  email?: string;
  name?: string;
  orgId?: string;
  orgName?: string;
  // HMAC-SHA256(email) signed by the server. Required for RogerIQ Secure Mode
  // — without it, the widget API will refuse to trust the identify payload.
  signature?: string;
};

let identityContext: IdentityContext = {};

function getWidget(): WidgetFn | undefined {
  if (typeof window === "undefined") return undefined;
  return (window as WidgetWindow).RogerIQ;
}

export function setSupportIdentity(next: IdentityContext) {
  identityContext = next;
  const widget = getWidget();
  if (!widget) return;

  if (next.email) {
    widget("identify", {
      email: next.email,
      ...(next.name ? { name: next.name } : {}),
      ...(next.signature ? { signature: next.signature } : {}),
    });
  }

  const sessionData: Record<string, string> = {};
  if (next.orgId) sessionData["B3OS Org ID"] = next.orgId;
  if (next.orgName) sessionData["B3OS Org Name"] = next.orgName;
  if (Object.keys(sessionData).length > 0) {
    widget("session-data", sessionData);
  }
}

export function clearSupportIdentity() {
  identityContext = {};
  const widget = getWidget();
  widget?.("logout");
  widget?.("reset");
}

function formatSubject(subject: string | undefined, orgId: string | undefined): string | undefined {
  if (!subject) {
    return orgId ? `Support request [Org: ${orgId}]` : undefined;
  }
  if (!orgId || subject.includes("[Org:")) return subject;
  return `${subject} [Org: ${orgId}]`;
}

export type SupportOpenOptions = {
  subject?: string;
  text?: string;
};

export function openSupport(options: SupportOpenOptions = {}) {
  const widget = getWidget();
  if (!widget) return;

  // Always overwrite subject/text so stale values from a previous error-context
  // open don't leak into a subsequent generic support ticket. Widget persists
  // prefill state across opens, so missing fields must be explicitly blanked.
  const prefill: Record<string, string> = {
    subject: formatSubject(options.subject, identityContext.orgId) ?? "",
    text: options.text ?? "",
  };
  if (identityContext.email) prefill.email = identityContext.email;
  if (identityContext.name) prefill.name = identityContext.name;

  widget("prefill", prefill);
  widget("open");
  widget("navigate", "/ask/message");
}
